// @ds/connected/ChecklistRow — shared row primitive for the
// `checklistSection()` factory in `@ds/connected/sharedSections`.
//
// Split out of `renderLayout.tsx` so that dispatcher module holds no component.

import React from "react";
import { Box } from "@ds/layout/Box";
import { Stack } from "@ds/layout/Stack";
import { Inline } from "@ds/layout/Inline";
import { TextValue } from "@ds/display/TextValue";
import { Icon, type IconName, type IconTone } from "@ds/display/Icon";

/** Status enum every checklist consumer (invoices / edpr / transactions) declares against. */
export type ChecklistRowStatus =
  | "done"
  | "pending"
  | "blocked"
  | "skipped"
  | "not_applicable";

const STATUS_ICON: Record<ChecklistRowStatus, IconName> = {
  done:           "checkCircle",
  pending:        "history",
  blocked:        "xCircle",
  skipped:        "close",
  not_applicable: "close",
};

const STATUS_TONE: Record<ChecklistRowStatus, IconTone> = {
  done:           "green",
  pending:        "amber",
  blocked:        "red",
  skipped:        "grey",
  not_applicable: "grey",
};

export interface ChecklistRowProps {
  id: string;
  label: string;
  status: ChecklistRowStatus;
  detail?: string;
  onClick?: () => void;
  testId?: string;
}

/**
 * Single checklist step. Renders status icon + label + optional detail.
 * Exported so `checklistSection()` (sharedSections) can render its custom
 * Stack of steps without re-implementing the row layout.
 */
export function ChecklistRow({
  id,
  label,
  status,
  detail,
  onClick,
  testId,
}: ChecklistRowProps): React.ReactElement {
  const iconName = STATUS_ICON[status];
  const iconTone = STATUS_TONE[status];
  const rowTestId = testId ?? `checklist-step-${id}`;
  return (
    <Box onClick={onClick} testId={rowTestId} fullWidth>
      <Inline gap="sm" align="start">
        <Box shrink>
          <Icon name={iconName} size="md" tone={iconTone} />
        </Box>
        <Box grow>
          <Stack gap="none">
            <TextValue as="div" size="sm" tone="primary">
              {label}
            </TextValue>
            {detail ? (
              <TextValue as="div" size="xs" tone="muted">
                {detail}
              </TextValue>
            ) : null}
          </Stack>
        </Box>
      </Inline>
    </Box>
  );
}
